import { canSeeSection, isSectionVisible, SectionVisibilityConfig } from '../../helpers/permissionHelper';
import { canSeeCostReportPage, canSeeOwnWorkCalendar, canSeeTimeBankReportPage, canSeeTimesheetPage, canSeeVacationReportPage } from './entityPermissions';
import { PROFILE_PERMISSION_CODES } from './permissionCodes';
import { Part, Profile } from './types';

export interface VisibilityAwareNavigationItem {
    visibility?: SectionVisibilityConfig | null;
}

const createSectionVisibilityConfig = (
    permissionCodes: string[],
    fallbackParts: Part[] = []
): SectionVisibilityConfig => ({
    permissionCodes,
    fallbackParts,
});

export const NAVIGATION_SECTION_VISIBILITY = {
    types: createSectionVisibilityConfig(
        [
            PROFILE_PERMISSION_CODES.SHOW_PROJECT_TYPES,
            PROFILE_PERMISSION_CODES.EDIT_PROJECT_TYPES,
            PROFILE_PERMISSION_CODES.SHOW_SHIFT_TYPES,
            PROFILE_PERMISSION_CODES.EDIT_SHIFT_TYPES,
            PROFILE_PERMISSION_CODES.SHOW_ABSENCE_TYPES,
            PROFILE_PERMISSION_CODES.EDIT_ABSENCE_TYPES,
            PROFILE_PERMISSION_CODES.SHOW_TAG_TYPES,
            PROFILE_PERMISSION_CODES.EDIT_TAG_TYPES,
        ],
        [Part.ProjectTypes, Part.ShiftTypes, Part.AbsenceTypes, Part.TagTypes]
    ),
    roles: createSectionVisibilityConfig(
        [
            PROFILE_PERMISSION_CODES.SHOW_ROLES,
            PROFILE_PERMISSION_CODES.EDIT_ROLES,
            PROFILE_PERMISSION_CODES.SHOW_PERMISSIONS,
            PROFILE_PERMISSION_CODES.CHANGE_PERMISSIONS,
        ],
        [Part.Roles]
    ),
    employees: createSectionVisibilityConfig(
        [
            PROFILE_PERMISSION_CODES.SHOW_EMPLOYEES,
            PROFILE_PERMISSION_CODES.EDIT_EMPLOYEES,
            PROFILE_PERMISSION_CODES.CHANGE_EMPLOYEE_ROLES,
        ],
        [Part.Employees]
    ),
    clients: createSectionVisibilityConfig(
        [PROFILE_PERMISSION_CODES.SHOW_CLIENTS, PROFILE_PERMISSION_CODES.EDIT_CLIENTS],
        [Part.Clients]
    ),
    projects: createSectionVisibilityConfig(
        [PROFILE_PERMISSION_CODES.SHOW_PROJECTS, PROFILE_PERMISSION_CODES.EDIT_PROJECTS],
        [Part.Projects]
    ),
    timesheet: createSectionVisibilityConfig(
        [
            PROFILE_PERMISSION_CODES.ACCESS_TO_TIMESHEET,
            PROFILE_PERMISSION_CODES.SHOW_SUMMARY_FOR_ALL_EMPLOYEES,
            PROFILE_PERMISSION_CODES.EDIT_SHIFTS,
            PROFILE_PERMISSION_CODES.EDIT_OLD_SHIFTS,
            PROFILE_PERMISSION_CODES.CAN_APPROVE,
            PROFILE_PERMISSION_CODES.CAN_DISAPPROVE,
            PROFILE_PERMISSION_CODES.CAN_BILL,
            PROFILE_PERMISSION_CODES.CAN_UNBILL,
            PROFILE_PERMISSION_CODES.IMPORT_TIME_TRACKING,
        ],
        [Part.Timesheet]
    ),
    vacation: createSectionVisibilityConfig(
        [PROFILE_PERMISSION_CODES.SHOW_ALL_VACATIONS],
        [Part.Vacation]
    ),
    cost: createSectionVisibilityConfig(
        [
            PROFILE_PERMISSION_CODES.SHOW_COSTS_FOR_ALL_EMPLOYEES,
            PROFILE_PERMISSION_CODES.SHOW_AND_EDIT_COST_FOR_ALL_EMPLOYEES,
        ],
        [Part.Cost]
    ),
    timeBank: createSectionVisibilityConfig(
        [
            PROFILE_PERMISSION_CODES.SHOW_TIME_BANK_FOR_ALL_EMPLOYEES,
            PROFILE_PERMISSION_CODES.SHOW_AND_EDIT_TIME_BANK_FOR_ALL_EMPLOYEES,
        ],
        [Part.TimeBank]
    ),
    settingsPreferences: createSectionVisibilityConfig(
        [
            PROFILE_PERMISSION_CODES.EDIT_JOB_EXECUTION_TIME,
            PROFILE_PERMISSION_CODES.EDIT_TIMEZONE,
        ],
        [Part.StartAndEndDayTime]
    ),
    settingsIntegrations: createSectionVisibilityConfig(
        [
            PROFILE_PERMISSION_CODES.SHOW_INTEGRATIONS,
            PROFILE_PERMISSION_CODES.EDIT_INTEGRATIONS,
        ],
        [Part.Integrations]
    ),
    settingsEarlyAccess: createSectionVisibilityConfig(
        [
            PROFILE_PERMISSION_CODES.SHOW_EARLY_ACCESS,
            PROFILE_PERMISSION_CODES.EDIT_EARLY_ACCESS,
        ],
        [Part.EarlyAccess]
    ),
    settingsCalendar: createSectionVisibilityConfig(
        [
            PROFILE_PERMISSION_CODES.SHOW_WORK_CALENDARS,
            PROFILE_PERMISSION_CODES.EDIT_WORK_DAYS,
        ],
        [Part.WorkCalendar]
    ),
    settingsLegacyOnly: {
        fallbackParts: [Part.NotificationAtEndOfMonth, Part.Payments],
    } as SectionVisibilityConfig,
    subscription: createSectionVisibilityConfig(
        [PROFILE_PERMISSION_CODES.CAN_PAY_SUBSCRIPTION],
        [Part.Subscription]
    ),
};

export const SETTINGS_TAB_VISIBILITY = {
    preferences: NAVIGATION_SECTION_VISIBILITY.settingsPreferences,
    integrations: NAVIGATION_SECTION_VISIBILITY.settingsIntegrations,
    earlyAccess: NAVIGATION_SECTION_VISIBILITY.settingsEarlyAccess,
    calendar: NAVIGATION_SECTION_VISIBILITY.settingsCalendar,
};

export function getVisibleNavigationItems<T extends VisibilityAwareNavigationItem>(
    profile: Profile | null | undefined,
    items: T[]
): T[] {
    return items.filter((item) => isSectionVisible(profile, item.visibility));
}

export function canSeeTypesSection(profile: Profile | null | undefined): boolean {
    return isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.types);
}

export function canSeeRolesSection(profile: Profile | null | undefined): boolean {
    return isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.roles);
}

export function canSeeEmployeesSection(profile: Profile | null | undefined): boolean {
    return isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.employees);
}

export function canSeeClientsSection(profile: Profile | null | undefined): boolean {
    return isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.clients);
}

export function canSeeProjectsSection(profile: Profile | null | undefined): boolean {
    return isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.projects);
}

export function canSeeTimesheetSection(profile: Profile | null | undefined): boolean {
    return isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.timesheet);
}

export function canSeeVacationSection(profile: Profile | null | undefined): boolean {
    return canSeeVacationReportPage(profile);
}

export function canSeeCostSection(profile: Profile | null | undefined): boolean {
    return isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.cost);
}

export function canSeeTimeBankSection(profile: Profile | null | undefined): boolean {
    return canSeeTimeBankReportPage(profile);
}

export function canSeeScheduleSection(profile: Profile | null | undefined): boolean {
    return canSeeTimesheetPage(profile)
        || canSeeVacationReportPage(profile)
        || canSeeCostReportPage(profile)
        || canSeeTimeBankReportPage(profile);
}

export function canSeeSettingsSection(profile: Profile | null | undefined): boolean {
    return canSeeSection(profile, SETTINGS_TAB_VISIBILITY.preferences.permissionCodes ?? [], SETTINGS_TAB_VISIBILITY.preferences.fallbackParts)
        || canSeeSection(profile, SETTINGS_TAB_VISIBILITY.integrations.permissionCodes ?? [], SETTINGS_TAB_VISIBILITY.integrations.fallbackParts)
        || canSeeSection(profile, SETTINGS_TAB_VISIBILITY.earlyAccess.permissionCodes ?? [], SETTINGS_TAB_VISIBILITY.earlyAccess.fallbackParts)
        || canSeeOwnWorkCalendar(profile)
        || isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.settingsLegacyOnly);
}

export function canSeeSubscriptionSection(profile: Profile | null | undefined): boolean {
    return isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.subscription);
}
